import type { Product } from "schemas/generated/product";

import React from "react";

import { productRouter } from "@editor/trpc/product";

import { skipToken } from "@tanstack/react-query";

interface UseProductListOptions {
  projectId?: string;
  limit?: number;
  search?: string;
  enabled?: boolean;
}

interface UseProductListReturn {
  products: Product[];
  isLoading: boolean;
  error: unknown;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

/**
 * Custom hook for fetching and managing product lists with infinite pagination.
 * Consolidates product fetching logic used across modifiers and chat context menus.
 * Uses semantic search when a search term is provided for better performance and results.
 *
 * @param options - Configuration options for the product query
 * @returns Product list data and pagination controls
 */
export function useProductList({
  projectId,
  limit = 20,
  search,
  enabled = true,
}: UseProductListOptions): UseProductListReturn {
  const searchTerm = search ?? "";

  const {
    data: semanticSearchData,
    isLoading: isSemanticLoading,
    error: semanticError,
    fetchNextPage: fetchNextSemanticPage,
    hasNextPage: hasNextSemanticPage,
    isFetchingNextPage: isFetchingNextSemanticPage,
  } = productRouter.find.useInfiniteQuery(
    projectId && enabled && searchTerm
      ? {
          config: {
            projectId,
            filters: {
              searchTerm,
              semantic: true,
            },
          },
          pageSize: limit,
        }
      : skipToken,
    {
      enabled: Boolean(projectId && enabled && searchTerm),
      staleTime: 0,
      getNextPageParam: ({ nextCursor }) => nextCursor,
    },
  );

  const {
    data: listData,
    isLoading: isListLoading,
    error: listError,
    fetchNextPage: fetchNextListPage,
    hasNextPage: hasNextListPage,
    isFetchingNextPage: isFetchingNextListPage,
  } = productRouter.list.useInfiniteQuery(
    projectId && enabled && !searchTerm
      ? {
          projectId,
          limit,
          writeThroughProductsSync: true,
          filters: {
            searchTerm,
          },
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
    },
  );

  const products = React.useMemo(() => {
    if (searchTerm) {
      return semanticSearchData?.pages?.flatMap((page) => page.products) ?? [];
    }
    return listData?.pages?.flatMap((page) => page.products) ?? [];
  }, [searchTerm, semanticSearchData, listData]);

  return {
    products,
    isLoading: searchTerm ? isSemanticLoading : isListLoading,
    error: searchTerm ? semanticError : listError,
    fetchNextPage: searchTerm
      ? () => void fetchNextSemanticPage()
      : () => void fetchNextListPage(),
    hasNextPage: searchTerm
      ? hasNextSemanticPage ?? false
      : hasNextListPage ?? false,
    isFetchingNextPage: searchTerm
      ? isFetchingNextSemanticPage
      : isFetchingNextListPage,
  };
}
